import { datadogRum, RumInitConfiguration } from '@datadog/browser-rum';
import { datadogLogs, LogsInitConfiguration } from '@datadog/browser-logs';
import type { Context } from '@datadog/browser-core';
import type { RumPublicApi } from '@datadog/browser-rum-core';

import {
  IS_FEDERAL,
  APP_NAME,
  DATADOG_RUM_APP_ID,
  DATADOG_RUM_CLIENT_TOKEN,
  REMOTASK_DATADOG_RUM_CLIENT_TOKEN,
  REMOTASK_DATADOG_RUM_APP_ID,
  NODE_ENV,
  IS_DEV,
  DATADOG_CLIENT_TOKEN,
  SERVICE_NAME,
} from './constants';
import { useRef } from 'react';
import { useRouteMatch, Route } from 'react-router-dom';
import _ from 'lodash';

// Useful examples https://github.com/DataDog/rum-react-integration-examples
declare global {
  interface Window {
    DD_RUM?: RumPublicApi & {
      startView?(name?: string): void;
    };
  }
}

export const SKIPPED_ERRORS = [
  'Script error.',
  "csp_violation: 'eval' blocked by 'script-src' directive",
  'ResizeObserver loop completed with undelivered notifications.',
  'console error: Error: ',
];

const CONTEXT_KEY_VIEW = 'current_view';

const DEFAULT_CONFIG: RumInitConfiguration = {
  env: NODE_ENV,
  applicationId: DATADOG_RUM_APP_ID,
  clientToken: DATADOG_RUM_CLIENT_TOKEN,
  site: 'datadoghq.com',
  service: 'scale.com',
  sessionSampleRate: 100,
  trackUserInteractions: true,
  trackViewsManually: true,
  excludedActivityUrls: [
    // regexp: begins with 'https://j.clarity.ms'
    /^https:\/\/j\.clarity\.ms/,
    // regexp: begins with 'https://rs.fullstory.com'
    /^https:\/\/rs\.fullstory\.com/,
  ],
};

// https://docs.datadoghq.com/real_user_monitoring/browser/setup/#setup
const REMOTASK_RUM_CONFIG: RumInitConfiguration = {
  // in secret manager in prod/frontend/services
  // read from webapp/config/default.yaml
  // remotasks-rum:
  // applicationId:
  // clientToken:
  applicationId: REMOTASK_DATADOG_RUM_APP_ID,
  clientToken: REMOTASK_DATADOG_RUM_CLIENT_TOKEN,
  site: 'datadoghq.com',
  service: 'remotasks',
  env: process.env.NODE_ENV ?? 'development',
  version: process.env.BUILD_ID,
  sessionSampleRate: 10,
  sessionReplaySampleRate: 10,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  enableExperimentalFeatures: ['feature_flags'],
};

const DEFAULT_LOGGER_CONFIG: LogsInitConfiguration = {
  env: NODE_ENV,
  service: SERVICE_NAME,
  clientToken: DATADOG_CLIENT_TOKEN,
  forwardErrorsToLogs: false,
  silentMultipleInit: true,
};

let isDatadogInitialized = false;
let isDatadogLogsInitialized = false;

export function isDatadogRumInitialized(): boolean {
  return isDatadogInitialized;
}

export function isDatadogLoggerInitialized(): boolean {
  return isDatadogLogsInitialized;
}

export default function initDatadog(overrideConfig: Partial<RumInitConfiguration>) {
  if (IS_FEDERAL || isDatadogInitialized) {
    return;
  }

  if (APP_NAME && APP_NAME.includes('remotasks')) {
    datadogRum.init(REMOTASK_RUM_CONFIG);
    isDatadogInitialized = true;
    return;
  }

  const ddRumConfig: RumInitConfiguration = {
    ...DEFAULT_CONFIG,
    ...overrideConfig,
  };

  // Configuration copied from Datadog's tool:
  // https://docs.datadoghq.com/real_user_monitoring/browser/#npm
  datadogRum.init(ddRumConfig);
  isDatadogInitialized = true;
}

// Tracks Datadog RUM view
export function rumStartView(viewName: string) {
  if (IS_DEV) {
    // eslint-disable-next-line
    console.log('Start rum view', viewName);
  }
  // Sets view name context
  datadogRum.addRumGlobalContext('viewName', viewName);
  // Clears old trigger
  clearTimeout(datadogRum.getRumGlobalContext().viewTrigger as number);
  // Clears out view specific context
  rumClearViewContext();
  // Adds new trigger
  datadogRum.addRumGlobalContext(
    'viewTrigger',
    setTimeout(() => datadogRum.startView(viewName)),
  );
}

export function rumSetUser(
  user?: {
    id?: string | null;
    spoofed?: boolean | null;
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  } | null,
) {
  if (IS_DEV) {
    // eslint-disable-next-line
    console.log('Setting user in Datadog RUM', user);
  }
  datadogRum.setUser({
    id: user?.id || undefined,
    spoofed: user?.spoofed,
    email: user?.email || undefined,
    name: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : undefined,
  });
}

// Add context specific to the current view
export function rumAddViewContext(context: Record<string, string | number>) {
  const currentContext = datadogRum.getRumGlobalContext();
  const currentViewTags = (currentContext[CONTEXT_KEY_VIEW] as Context) ?? {};
  datadogRum.addRumGlobalContext(CONTEXT_KEY_VIEW, { ...context, ...currentViewTags });
}

// Clear all context specific to the current view
function rumClearViewContext() {
  datadogRum.removeRumGlobalContext(CONTEXT_KEY_VIEW);
}

export function initDatadogLogs(overrideConfig: Partial<LogsInitConfiguration>) {
  if (IS_FEDERAL || isDatadogLogsInitialized) {
    return;
  }

  const ddLogConfig: LogsInitConfiguration = {
    ...DEFAULT_LOGGER_CONFIG,
    ...overrideConfig,
  };

  datadogLogs.init(ddLogConfig);
  isDatadogLogsInitialized = true;
}

export function getLogger() {
  return datadogLogs.logger;
}

export function addFeatureFlagEvaluation(key: string, value: boolean) {
  if (!isDatadogInitialized) {
    return;
  }

  datadogRum.addFeatureFlagEvaluation(key, value);
}

export function startSessionReplayRecording() {
  if (!isDatadogInitialized) {
    return;
  }
  datadogRum.startSessionReplayRecording();
}

const useRumRouteTracking = () => {
  const prevMatch = useRef<ReturnType<typeof useRouteMatch> | null>(null);

  return function checkAndUpdateRumView(match: ReturnType<typeof useRouteMatch>, viewPath: string) {
    if (!_.isEqual(match, prevMatch.current)) {
      prevMatch.current = match;
      rumStartView(viewPath.toString());
    }
  };
};

function RumRoute<T>({
  path,
  exact,
  renderElement,
}: {
  path: string | string[];
  exact: boolean;
  renderElement: ((params: T) => React.ReactElement) | React.ReactElement;
}) {
  const checkAndUpdateRumView = useRumRouteTracking();

  return (
    <Route
      path={path}
      exact={exact}
      render={({ match }) => {
        checkAndUpdateRumView(match, path.toString());

        const element =
          typeof renderElement === 'function' ? renderElement(match.params as T) : renderElement;
        return element;
      }}
    />
  );
}

export function createRumRoute<T = any>(
  path: string | string[],
  renderElement: ((params: T) => React.ReactElement) | React.ReactElement,
  exact: boolean = true,
) {
  return <RumRoute path={path} exact={exact} renderElement={renderElement} />;
}
